

import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";
import { useTranslation } from "react-i18next";

const toPreview = (f) => ({
  url: URL.createObjectURL(f),
  type: f.type.startsWith("image/") ? "image" : "video",
  name: f.name,
  size: f.size,
});

/**
 * MediaPicker
 * - Accepts images and videos
 * - Validates size/limits
 * - Shows previews with remove buttons
 * - Calls onFilesChange(files) whenever selection changes
 * - Exposes ref.current.open() so parents (e.g., AppFooter button) can open the picker
 */
const MediaPicker = forwardRef(function MediaPicker(
  {
    onFilesChange,
    multiple = true,
    maxImageMB = 10,
    maxVideoMB = 200,
    maxFiles = 50,
    label = "Add images/videos",
    accept = "image/*,video/*",
  },
  ref
) {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const [mediaFiles, setMediaFiles] = useState([]); // File[]
  const [previews, setPreviews] = useState([]);     // { url, type, name, size }[]
  const [rejections, setRejections] = useState([]); // string[]: why files were left out

  const openFileDialog = () => inputRef.current?.click();

  useImperativeHandle(ref, () => ({
    open: openFileDialog,
    getFiles: () => mediaFiles,
    clear: () => {
      previews.forEach((p) => p?.url && URL.revokeObjectURL(p.url));
      setPreviews([]);
      setMediaFiles([]);
      setRejections([]);
      if (inputRef.current) inputRef.current.value = "";
      onFilesChange?.([]);
    },
    // Replace the selection, e.g. with just the files a failed upload still needs.
    setFiles: (files) => {
      previews.forEach((p) => p?.url && URL.revokeObjectURL(p.url));
      setPreviews(files.map(toPreview));
      setMediaFiles(files);
      setRejections([]);
      if (inputRef.current) inputRef.current.value = "";
      onFilesChange?.(files);
    },
  }));

  const handleSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const next = [];
    const nextPreviews = [];

    const currentCount = mediaFiles.length;
    const availableSlots = Math.max(0, maxFiles - currentCount);
    const limited = files.slice(0, availableSlots);
    const nextRejections = [];
    if (limited.length < files.length) {
      nextRejections.push(t("events.mediaTooManyFiles", { max: maxFiles }));
    }

    for (const f of limited) {
      const isImage = f.type.startsWith("image/");
      const isVideo = f.type.startsWith("video/");
      if (!isImage && !isVideo) {
        nextRejections.push(t("events.mediaFileUnsupported", { name: f.name }));
        continue;
      }

      const sizeMB = f.size / (1024 * 1024);
      const maxMB = isImage ? maxImageMB : maxVideoMB;
      if (sizeMB > maxMB) {
        nextRejections.push(t("events.mediaFileTooLarge", { name: f.name, max: maxMB }));
        continue;
      }

      next.push(f);
      nextPreviews.push(toPreview(f));
    }

    const newFiles = [...mediaFiles, ...next];
    const newPreviews = [...previews, ...nextPreviews];
    setMediaFiles(newFiles);
    setPreviews(newPreviews);
    setRejections(nextRejections);
    onFilesChange?.(newFiles);
  };

  const removeAt = (i) => {
    const p = previews[i];
    if (p?.url) URL.revokeObjectURL(p.url);
    const newPreviews = previews.filter((_, idx) => idx !== i);
    const newFiles = mediaFiles.filter((_, idx) => idx !== i);
    setPreviews(newPreviews);
    setMediaFiles(newFiles);
    onFilesChange?.(newFiles);
  };

  // cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      previews.forEach((p) => p?.url && URL.revokeObjectURL(p.url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      {/* <button type="button" onClick={openFileDialog}>
        {label}
      </button> */}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        style={{ display: "none" }}
        onChange={handleSelect}
      />

      {rejections.length > 0 && (
        <div className="alert alert-danger" role="alert" style={{ marginTop: 12 }}>
          {rejections.map((msg) => (
            <div key={msg}>{msg}</div>
          ))}
        </div>
      )}

      {previews.length > 0 && (
        <div
          className="media-previews"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
            gap: 12,
            marginTop: 12,
          }}
        >
          {previews.map((m, i) => (
            <div key={i} style={{ border: "1px solid #333", borderRadius: 8, padding: 8 }}>
              {m.type === "image" ? (
                <img
                  src={m.url}
                  alt={m.name}
                  style={{ width: "100%", height: 120, objectFit: "cover", borderRadius: 6 }}
                />
              ) : (
                <video
                  src={m.url}
                  controls
                  style={{ width: "100%", height: 120, borderRadius: 6 }}
                />
              )}
              <div
                style={{
                  marginTop: 6,
                  fontSize: 12,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                title={m.name}
              >
                {m.name}
              </div>
              <button
                type="button"
                onClick={() => removeAt(i)}
                style={{ marginTop: 6, width: "100%" }}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

export default MediaPicker;
